import { Select } from '@bfe/moly';
import { NoSSR } from '@web3/base-components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getSwapOrderListApi, GetSwapOrderListParams } from '@/services/swap';
import { useCurrentWallet } from '@web3fe/infra';
import { WalletChainTypes } from '@web3/base-utils';
import { useFm, useToast, useParams } from '@web3/base-hooks';
import OrderList from './components/OrderList';
import { useSwapChainList } from './hooks';
import { useRequest } from 'ahooks';

export const SwapOrder = () => {
  const t = useFm();
  const { toastWarn } = useToast();
  const { chainId } = useParams();
  const { evmAddress, solAddress, suiAddress, tonAddress, tronAddress } =
    useCurrentWallet();
  const { swapChainList } = useSwapChainList();
  const [selectedChainId, setSelectedChainId] = useState<number>(
    chainId ? +chainId : -1
  );
  const [orderList, setOrderList] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const offsetId = useRef(0);

  useEffect(() => {
    if (!selectedChainId) return;
    history.replaceState(null, '', `?chainId=${selectedChainId}`);
  }, [selectedChainId]);

  /**
   * 链选项
   */
  const chainListOptions = useMemo(() => {
    const allChain = {
      label: t('all'),
      value: -1,
    };
    if (!swapChainList?.length) {
      return [allChain];
    }
    return [
      allChain,
      ...swapChainList.map((val) => ({
        label: val.chainName,
        value: val.chainId,
      })),
    ];
  }, [swapChainList, t]);

  /** 更新地址栏链Id参数，防止刷新重置 */
  const handleChainChange = (value: string) => {
    offsetId.current = 0;
    setHasMore(false);
    setOrderList([]);
    setSelectedChainId(+value);
  };

  /**
   * 钱包地址列表
   */
  const walletAddresses = useMemo(() => {
    if (!swapChainList?.length) return [];
    return swapChainList.reduce((res, val) => {
      if (val.chainType === WalletChainTypes.EVM && evmAddress) {
        res.push({
          chainId: val.chainId,
          walletAddress: evmAddress,
        });
      }
      if (val.chainType === WalletChainTypes.SOLANA && solAddress) {
        res.push({
          chainId: val.chainId,
          walletAddress: solAddress,
        });
      }
      if (val.chainType === WalletChainTypes.SUI && suiAddress) {
        res.push({
          chainId: val.chainId,
          walletAddress: suiAddress,
        });
      }
      if (val.chainType === WalletChainTypes.TON && tonAddress) {
        res.push({
          chainId: val.chainId,
          walletAddress: tonAddress,
        });
      }
      if (val.chainType === WalletChainTypes.TRON && tronAddress) {
        res.push({
          chainId: val.chainId,
          walletAddress: tronAddress,
        });
      }
      return res;
    }, []);
  }, [
    swapChainList,
    evmAddress,
    solAddress,
    suiAddress,
    tonAddress,
    tronAddress,
  ]);

  /** 获取订单列表 */
  const { run: handleGetOrderList, loading } = useRequest(
    async (id: number = 0) => {
      if (!id) setOrderList([]);
      if (loading || !walletAddresses.length) return;
      if (
        selectedChainId !== -1 &&
        !walletAddresses.find((val) => val.chainId === selectedChainId)
      )
        return;
      const params: GetSwapOrderListParams = {
        direct: 1,
        offsetLimit: 11,
        offsetId: id,
        walletAddresses,
      };
      if (selectedChainId !== -1) {
        params.chainId = selectedChainId;
      }
      const { success, result, message } = await getSwapOrderListApi(params);
      if (!success) {
        toastWarn(message);
        return;
      }
      if (+result?.total === params.offsetLimit) {
        setHasMore(true);
        offsetId.current = result.orders[result.orders.length - 1].id;
      } else {
        setHasMore(false);
      }
      if (!params.offsetId) {
        setOrderList((result?.orders ?? []).slice(0, params.offsetLimit - 1));
      } else {
        setOrderList([
          ...orderList,
          ...(result?.orders ?? []).slice(0, params.offsetLimit - 1),
        ]);
      }
    },
    {
      debounceWait: 500,
      refreshDeps: [walletAddresses, selectedChainId],
      ready: walletAddresses.length > 0,
    }
  );

  return (
    <>
      <div className="flex items-center">
        <div className="flex flex-col gap-1.5">
          <label className="text-base font-medium text-bds-gray-t2">
            {t('network')}
          </label>
          <NoSSR>
            <Select
              value={selectedChainId}
              onChange={handleChainChange}
              size={'xs'}
              width="130px"
              options={chainListOptions}
            />
          </NoSSR>
        </div>
      </div>
      <div className="p-6 bg-bds-gray-bg-card rounded-lg flex-1 flex flex-col">
        <div className="flex-1">
          <OrderList
            loading={loading}
            data={orderList}
            hasMore={hasMore}
            loadMore={() => handleGetOrderList(offsetId.current)}
          />
        </div>
        <p className="text-sm font-bold p-3">
          {t('showOrderHistoryLast90Days')}
        </p>
      </div>
    </>
  );
};
